import type { tickets } from '@repletoryreach/sdk-api';

/** A stable key from a field's label: lower case, ASCII, underscores; unique among `taken`. */
export function fieldKey(label: string, taken: readonly string[] = []): string {
  const base = label
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 40) || 'field';
  if (!taken.includes(base)) return base;
  for (let i = 2; ; i++) {
    const k = `${base}_${i}`;
    if (!taken.includes(k)) return k;
  }
}

/** Whether a field's value counts as filled in. */
export function isFilled(value: unknown): boolean {
  if (value === null || value === undefined) return false;
  if (typeof value === 'string') return value.trim() !== '';
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === 'number') return Number.isFinite(value);
  return true;
}

/** The required fields of a type that have no value yet. */
export function missingRequired(fields: readonly tickets.TypeField[], values: Record<string, unknown> | null | undefined): tickets.TypeField[] {
  return fields.filter((f) => f.required && !isFilled(values?.[f.key]));
}

/** A value as the service stores it for the field's type: numbers as numbers, yes/no as booleans, lists as lists. */
export function normaliseValue(type: tickets.FieldType, value: unknown): unknown {
  if (value === '' || value === undefined) return null;
  switch (type) {
    case 'number': {
      const n = typeof value === 'number' ? value : Number(String(value).replace(',', '.'));
      return Number.isFinite(n) ? n : null;
    }
    case 'yes_no':
      if (typeof value === 'boolean') return value;
      return value === 'true' || value === 'yes' ? true : value === 'false' || value === 'no' ? false : null;
    case 'multi_select':
      return Array.isArray(value) ? value.map(String) : String(value).split(',').map((s) => s.trim()).filter(Boolean);
    default:
      return typeof value === 'string' ? value.trim() || null : value;
  }
}

/** Options typed as "a, b, c": trimmed and de-duplicated. */
export function parseOptions(text: string): string[] {
  const out: string[] = [];
  for (const o of text.split(',')) {
    const v = o.trim();
    if (v && !out.includes(v)) out.push(v);
  }
  return out;
}
